import * as React from "react"
import EditConnectionTypesPage from "@/routes/_home/meter-connection-settings/connection-type/edit"
import { ConnectionType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { validateUserDeletePermission } from "@/lib/api/auth"
import { deleteConnectionType } from "@/lib/api/connection-type"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
} from "@/components/ui/credenza"
import { Dialog } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Icons } from "@/components/icons"

interface DataProps {
  data: ConnectionType
}

export function ConnectionTypesOperations({ data }: DataProps) {
  const [showDropDown, setShowDropDown] = React.useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false)
  const [showEditModal, setShowEditModal] = React.useState<boolean>(false)
  return (
    <div>
      <DropdownMenu open={showDropDown} onOpenChange={setShowDropDown}>
        <DropdownMenuTrigger>
          <div className="flex h-8 w-8 items-center justify-center rounded-md border transition-colors hover:bg-muted">
            <Icons.ellipsis className="size-4" />
            <span className="sr-only">Open</span>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              setShowEditModal(true)
              setShowDropDown(false)
            }}
          >
            <Icons.edit className="mr-2 size-4" />
            Edit Connection Type
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem>
            <Icons.copy className="mr-2 size-4" />
            Copy Connection Types
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem>
            <Icons.paste className="mr-2 size-4" />
            Paste Connection Types
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive"
            onSelect={() => {
              setShowDeleteModal(true)
              setShowDropDown(false)
            }}
          >
            <Icons.archive className="mr-2 size-4" />
            Archive
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Edit Modal */}
      <Credenza open={showEditModal} onOpenChange={setShowEditModal}>
        <EditConnectionTypesPage
          connectionTypeData={data}
          setShowEditConnectionTypeModal={setShowEditModal}
        />
      </Credenza>

      <DeleteConnectionType
        connectionTypeId={data.id ?? 0}
        connectionType={data.type_name ?? 0}
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
      />
    </div>
  )
}

function DeleteConnectionType({
  connectionTypeId,
  connectionType,
  showModal,
  setShowModal,
}: {
  connectionTypeId: number
  connectionType: string
  showModal: boolean
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const formSchema = z.object({
    id: z.coerce.number(),
    selected_connection_type: z
      .string()
      .min(1, "Required")
      .refine((val) => val === connectionType, {
        message: "Selected connection type must match the connection type.",
      }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: connectionTypeId,
      selected_connection_type: "",
    },
  })

  const mutation = useMutation({
    mutationFn: async () => {
      await deleteConnectionType(connectionTypeId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectionTypesDetails"] })
      toast({
        description: "Connection type deleted successfully.",
      })

      form.reset()
      setShowModal(false)
    },
    onError: (error: unknown) => {
      toast({
        title: "Something went wrong!",
        description: `${error}`,
        variant: "destructive",
      })
    },
  })

  async function onSubmit() {
    if (!validateUserDeletePermission()) return
    mutation.mutate()
  }

  return (
    <>
      <Credenza open={showModal} onOpenChange={setShowModal}>
        <CredenzaContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 pb-4"
            >
              <CredenzaHeader>
                <CredenzaTitle>Delete Connection Type</CredenzaTitle>
                <CredenzaDescription>
                  You cannot undo this action!
                </CredenzaDescription>
              </CredenzaHeader>
              <CredenzaBody>
                <FormField
                  control={form.control}
                  name="selected_connection_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type "{connectionType}" to delete.</FormLabel>
                      <FormControl>
                        <Input {...field} autoComplete="off" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CredenzaBody>
              <CredenzaFooter>
                <CredenzaClose asChild>
                  <Button variant={"outline"}>Cancel</Button>
                </CredenzaClose>
                <Button
                  disabled={mutation.isPending}
                  type="submit"
                  variant={"destructive"}
                >
                  {mutation.isPending && (
                    <Icons.loader className="mr-2 animate-spin" />
                  )}
                  Delete
                </Button>
              </CredenzaFooter>
            </form>
          </Form>
        </CredenzaContent>
      </Credenza>
    </>
  )
}
